import React, { useState, useEffect, useMemo, useCallback, useRef, useImperativeHandle, forwardRef } from 'react';
import { type PipelineNodeRelationship } from '../../hooks/usePipelineNodeRelationships';
import { type PipelineNode, type PipelineNodeMapOption, type PipelineNodeField } from '../../services/pipelineNode.service';
import { type ComboboxOption, Combobox } from '../../components/ui/combobox';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  DndContext,
  closestCenter,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Loader2, GripVertical, X, Plus, AlertTriangle, Sparkles } from 'lucide-react';
import { useMultiColumnDistribution } from '../../hooks/useMultiColumnDistribution';
import { isPendingRelationshipId, getPendingFieldKey } from '../../utils/relationshipFieldUtils';
import ApiService from '../../services/api.service';
import BackgroundService from '../../services/bg.service';
import { toast } from 'sonner';
import type { useSensors } from '@dnd-kit/core';

// Transformer options matching PipelineNodeFieldTransformer
const TransformerOptions: Array<{ label: string; description: string; value: string }> = [
  {
    label: 'Translate',
    description: 'Manually translate values using a Pliable-managed mapping table',
    value: 'TRANSLATE',
  },
  {
    label: 'Clean Zip Code',
    description: 'Cleans up zip codes by normalizing to 5 digits and ensuring there are leading zeroes',
    value: 'CLEAN_ZIP_CODE',
  },
  {
    label: 'Clean Phone Numbers',
    description: 'Normalizes phone numbers to (XXX) XXX-XXXX format',
    value: 'CLEAN_PHONE',
  },
  {
    label: 'Convert to Lowercase',
    description: 'Converts text values to all lowercase',
    value: 'LOWERCASE',
  },
  {
    label: 'Convert to Uppercase',
    description: 'Converts text values to all uppercase',
    value: 'UPPERCASE',
  },
  {
    label: 'Remove Non-Numeric Characters',
    description: 'Strips all characters that are not digits',
    value: 'REMOVE_NON_NUMERIC',
  },
  {
    label: 'Remove Special Characters',
    description: 'Strips all punctuation and special characters',
    value: 'REMOVE_SPECIAL_CHARS',
  },
  {
    label: 'Normalize Dates',
    description: 'Parses and cleans dates by trying a variety of common formats',
    value: 'PARSE_DATE',
  },
  {
    label: 'Convert to "Slug" Format',
    description: 'Converts text to a web-safe "slug" format, like "New York" -> "new-york"',
    value: 'SLUGIFY',
  },
  {
    label: 'Get URL Path',
    description: 'Extracts the path component from a URL (https://pliable.co/landing-page will return "/landing-page")',
    value: 'URL_PATH',
  },
  {
    label: 'Get URL Domain',
    description: 'Extracts the domain component from a URL (https://pliable.co/landing-page will return "pliable.co")',
    value: 'URL_DOMAIN',
  },
  {
    label: 'Custom',
    description: 'Write a custom transformation function using Snowflake SQL',
    value: 'CUSTOM',
  },
];

// Helper function to generate a stable key for map options
function getMapOptionKey(mapOption: PipelineNodeMapOption): string {
  if (mapOption.id) {
    return mapOption.id;
  }
  return `${mapOption.source_node_id || 'unknown'}:${mapOption.attribute_id || mapOption.attribute_key || 'unknown'}`;
}

// Sortable mapped field item component for dialog
interface SortableMappedFieldItemProps {
  id: string;
  nodeName: string;
  fieldName: string;
  fieldType: string;
  onRemove: () => void;
}

function SortableMappedFieldItem({
  id,
  nodeName,
  fieldName,
  fieldType,
  onRemove,
}: SortableMappedFieldItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="flex items-center gap-2 p-2 border rounded-md bg-muted"
    >
      <div
        {...attributes}
        {...listeners}
        className="cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        <GripVertical className="h-4 w-4 text-muted-foreground" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium truncate">{fieldName}</span>
          <span className="text-xs text-muted-foreground">•</span>
          <span className="text-xs text-muted-foreground truncate">{nodeName}</span>
        </div>
        <p className="text-xs text-muted-foreground">{fieldType}</p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 flex-shrink-0"
        onClick={onRemove}
      >
        <X className="h-3 w-3" />
      </Button>
    </div>
  );
}

const CommonValuesPanel = React.memo(function CommonValuesPanel({
  mapOptions,
  isTranslate,
  onAddTranslation,
}: {
  mapOptions: PipelineNodeMapOption[];
  isTranslate: boolean;
  onAddTranslation: (value: string) => void;
}) {
  const fields = useMemo(
    () =>
      mapOptions
        .filter((opt) => opt.source_node_id && opt.attribute_id)
        .map((opt) => ({
          pipelineNodeId: opt.source_node_id!,
          fieldId: opt.attribute_id!,
        })),
    [mapOptions]
  );

  const { data: distribution, isLoading } = useMultiColumnDistribution(fields, true);

  return (
    <div className="flex-1 border-l pl-4 py-4 min-w-0 flex flex-col">
      <h4 className="font-medium text-sm mb-2 flex-shrink-0">Common Values</h4>
      <p className="text-xs text-muted-foreground mb-3 flex-shrink-0">
        Value distribution across mapped upstream columns
      </p>
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : !distribution || distribution.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">No values found</p>
      ) : (
        <div className="overflow-y-auto min-h-0 flex-1 space-y-1">
          {distribution.map((item, idx) => (
            <div
              key={idx}
              className={`flex items-center justify-between px-2 py-1.5 rounded text-sm ${
                isTranslate
                  ? 'hover:bg-accent cursor-pointer'
                  : ''
              }`}
              onClick={isTranslate ? () => onAddTranslation(item.value) : undefined}
              title={isTranslate ? `Click to add "${item.value}" as a translation source` : undefined}
            >
              <span className="truncate mr-2 flex-1">{item.value || <span className="text-muted-foreground italic">null</span>}</span>
              <Badge variant="secondary" className="flex-shrink-0 tabular-nums">
                {item.total.toLocaleString()}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
});

export interface FieldFormData {
  name: string;
  description: string;
  type: string;
  map_options: PipelineNodeMapOption[] | undefined;
  transformer?: string;
  custom_transform_sql?: string;
  custom_sql?: string;
  translations?: Array<{ source: string; translation: string }>;
  part_of_composite_key?: boolean;
  merge_behavior?: string;
}

export interface EditColumnDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingField: NonNullable<PipelineNode['fields']>[0] | null;
  initialFormData: FieldFormData;
  initialRelationshipData: Partial<PipelineNodeRelationship> | null;
  originalEditingFieldName: string | null;
  // Read-only data for dropdowns
  currentNodeId: string | undefined;
  upstreamNodes: PipelineNode[];
  allNodes: PipelineNode[];
  currentNodeFields: PipelineNodeField[] | undefined;
  groupingBehavior: string | undefined;
  incrementalBuild?: boolean;
  mappedFieldComboboxOptions: ComboboxOption[];
  parentNodeOptions: ComboboxOption[];
  foreignKeyRelationshipOptions: ComboboxOption[];
  sensors: ReturnType<typeof useSensors>;
  // Lookup helpers
  getRelationship: (relationshipId: string) => any;
  pendingRelationships: Map<string, Partial<PipelineNodeRelationship>>;
  relationshipsToSave: Map<string, Partial<PipelineNodeRelationship>>;
  // Callbacks
  onSave: (params: {
    fieldFormData: FieldFormData;
    relationshipFormData: Partial<PipelineNodeRelationship> | null;
  }) => void;
  onDenormalizedRelationshipChange: (params: {
    oldRelationshipId: string | undefined;
    newRelationshipId: string;
    fieldName: string;
    editingField: NonNullable<PipelineNode['fields']>[0];
  }) => void;
  onDenormalizedParentColumnChange: (params: {
    relationshipId: string;
    parentFieldId: string;
    fieldName: string;
    originalFieldName: string;
  }) => void;
}

// Memoized text fields that own their own state so keystrokes
// don't re-render the entire dialog (which contains heavy Combobox/DnD trees).
interface IsolatedFieldHandle {
  getValue: () => string;
}

const IsolatedInput = React.memo(forwardRef<IsolatedFieldHandle, {
  initialValue: string;
  id?: string;
  placeholder?: string;
}>(function IsolatedInput({ initialValue, id, placeholder }, ref) {
  const [value, setValue] = useState(initialValue);
  useEffect(() => { setValue(initialValue); }, [initialValue]);
  useImperativeHandle(ref, () => ({ getValue: () => value }), [value]);
  return <Input id={id} value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} />;
}));

const IsolatedTextarea = React.memo(forwardRef<IsolatedFieldHandle, {
  initialValue: string;
  id?: string;
  placeholder?: string;
  rows?: number;
  className?: string;
}>(function IsolatedTextarea({ initialValue, id, placeholder, rows, className }, ref) {
  const [value, setValue] = useState(initialValue);
  useEffect(() => { setValue(initialValue); }, [initialValue]);
  useImperativeHandle(ref, () => ({ getValue: () => value }), [value]);
  return <Textarea id={id} value={value} onChange={(e) => setValue(e.target.value)} placeholder={placeholder} rows={rows} className={className} />;
}));

interface IsolatedTranslationsTableHandle {
  getValue: () => Array<{ source: string; translation: string }>;
  addRow: (source: string) => void;
  addRows: (rows: Array<{ source: string; translation: string }>) => void;
}

const IsolatedTranslationsTable = React.memo(forwardRef<IsolatedTranslationsTableHandle, {
  initialValue: Array<{ source: string; translation: string }>;
}>(function IsolatedTranslationsTable({ initialValue }, ref) {
  const [translations, setTranslations] = useState(initialValue);
  useEffect(() => { setTranslations(initialValue); }, [initialValue]);
  useImperativeHandle(ref, () => ({
    getValue: () => translations,
    addRow: (source: string) => setTranslations((prev) => [...prev, { source, translation: '' }]),
    addRows: (rows: Array<{ source: string; translation: string }>) => setTranslations((prev) => {
      const existingSources = new Set(prev.map((t) => t.source));
      const newRows = rows.filter((r) => !existingSources.has(r.source));
      return [...prev, ...newRows];
    }),
  }), [translations]);

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-[45%]">Source</TableHead>
          <TableHead className="w-[45%]">Translation</TableHead>
          <TableHead className="w-[10%] text-right">
            <Button variant="ghost" size="sm" onClick={() => setTranslations((prev) => [...prev, { source: '', translation: '' }])} className="h-8 w-8 p-0">
              <Plus className="h-4 w-4" />
            </Button>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {translations.length === 0 ? (
          <TableRow>
            <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
              No translation mappings. Click the + button to add one.
            </TableCell>
          </TableRow>
        ) : (
          translations.map((translation, idx) => (
            <TableRow key={idx}>
              <TableCell>
                <Input
                  value={translation.source}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTranslations((prev) => prev.map((t, i) => i === idx ? { ...t, source: val } : t));
                  }}
                  placeholder="Enter source value..."
                />
              </TableCell>
              <TableCell>
                <Input
                  value={translation.translation}
                  onChange={(e) => {
                    const val = e.target.value;
                    setTranslations((prev) => prev.map((t, i) => i === idx ? { ...t, translation: val } : t));
                  }}
                  placeholder="Enter translation..."
                />
              </TableCell>
              <TableCell className="text-right">
                <Button variant="ghost" size="sm" onClick={() => setTranslations((prev) => prev.filter((_, i) => i !== idx))} className="h-8 w-8 p-0">
                  <X className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}));

export function EditColumnDialog({
  open,
  onOpenChange,
  editingField,
  initialFormData,
  initialRelationshipData,
  originalEditingFieldName: propOriginalFieldName,
  currentNodeId,
  upstreamNodes,
  allNodes,
  currentNodeFields,
  groupingBehavior,
  incrementalBuild,
  mappedFieldComboboxOptions,
  parentNodeOptions,
  foreignKeyRelationshipOptions,
  sensors,
  getRelationship,
  pendingRelationships,
  relationshipsToSave,
  onSave,
  onDenormalizedRelationshipChange,
  onDenormalizedParentColumnChange,
}: EditColumnDialogProps) {
  const [fieldFormData, setFieldFormData] = useState<FieldFormData>(initialFormData);
  const nameRef = useRef<IsolatedFieldHandle>(null);
  const descriptionRef = useRef<IsolatedFieldHandle>(null);
  const customSqlRef = useRef<IsolatedFieldHandle>(null);
  const customTransformSqlRef = useRef<IsolatedFieldHandle>(null);
  const translationsRef = useRef<IsolatedTranslationsTableHandle>(null);
  const [relationshipFormData, setRelationshipFormData] = useState<Partial<PipelineNodeRelationship> | null>(initialRelationshipData);
  const [showCommonValues, setShowCommonValues] = useState(false);
  const [originalEditingFieldName, setOriginalEditingFieldName] = useState<string | null>(propOriginalFieldName);

  // Reset internal state when dialog opens with new data
  useEffect(() => {
    if (open) {
      setFieldFormData(initialFormData);
      setRelationshipFormData(initialRelationshipData);
      setShowCommonValues(false);
      setOriginalEditingFieldName(propOriginalFieldName);
    }
  }, [open, initialFormData, initialRelationshipData, propOriginalFieldName]);

  // Update fieldFormData when grouping behavior changes to STACK (if modal is open)
  // Don't clear composite keys if incremental build is enabled (keys are used for merge strategy)
  useEffect(() => {
    if (open && groupingBehavior === 'STACK' && !incrementalBuild) {
      setFieldFormData((prev) => {
        if (prev.part_of_composite_key) {
          return {
            ...prev,
            part_of_composite_key: false,
          };
        }
        return prev;
      });
    }
  }, [groupingBehavior, incrementalBuild, open]);

  const handleAddTranslationFromPanel = useCallback((value: string) => {
    translationsRef.current?.addRow(value);
  }, []);

  const [isAutoNormalizing, setIsAutoNormalizing] = useState(false);

  const handleAutoNormalize = useCallback(async () => {
    if (!fieldFormData.map_options || fieldFormData.map_options.length === 0) return;

    setIsAutoNormalizing(true);
    try {
      const fields = fieldFormData.map_options
        .filter((opt) => opt.source_node_id && opt.attribute_id)
        .map((opt) => ({
          pipeline_node_id: opt.source_node_id!,
          field_id: opt.attribute_id!,
        }));

      const resp = await ApiService.getInstance().request('POST', '/pipelinenodes/auto-normalize', { fields }) as { ok: boolean; job_id: string };

      const result = await BackgroundService.getInstance().waitForJob(resp.job_id, true) as { translations: Array<{ source: string; translation: string }>; error?: string };

      if (result.error) {
        toast.error(`Auto-normalize failed: ${result.error}`);
        return;
      }

      if (result.translations && result.translations.length > 0) {
        translationsRef.current?.addRows(result.translations);
        toast.success(`Added ${result.translations.length} normalization mappings`);
      } else {
        toast.info('No normalization suggestions found - values look clean!');
      }
    } catch (err: any) {
      toast.error(`Auto-normalize failed: ${err.message || 'Unknown error'}`);
    } finally {
      setIsAutoNormalizing(false);
    }
  }, [fieldFormData.map_options]);

  const handleSave = useCallback(() => {
    const currentTranslations = translationsRef.current?.getValue() ?? [];
    onSave({
      fieldFormData: {
        ...fieldFormData,
        name: nameRef.current?.getValue() ?? fieldFormData.name,
        description: descriptionRef.current?.getValue() ?? fieldFormData.description,
        custom_sql: customSqlRef.current?.getValue() ?? fieldFormData.custom_sql,
        custom_transform_sql: customTransformSqlRef.current?.getValue() ?? fieldFormData.custom_transform_sql,
        translations: currentTranslations.length > 0 ? currentTranslations : undefined,
      },
      relationshipFormData,
    });
  }, [fieldFormData, relationshipFormData, onSave]);

  const handleCancel = useCallback(() => {
    onOpenChange(false);
    setShowCommonValues(false);
    setRelationshipFormData(null);
  }, [onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      onOpenChange(isOpen);
      if (!isOpen) setShowCommonValues(false);
    }}>
      <DialogContent className={`${showCommonValues ? 'sm:max-w-[1400px]' : 'sm:max-w-[700px]'} max-h-[90vh] flex flex-col overflow-hidden transition-all`}>
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Edit Column</DialogTitle>
          <DialogDescription>
            {editingField && (editingField as any).advanced_mode === true
              ? 'Configure this calculated column'
              : fieldFormData.type === 'FOREIGN_KEY'
              ? 'Configure the relationship for this foreign key column.'
              : fieldFormData.type === 'DENORMALIZED'
              ? 'Configure this denormalized column.'
              : 'Configure this mapped column'}
          </DialogDescription>
        </DialogHeader>
        <div className={`min-h-0 ${showCommonValues ? 'flex gap-4' : 'overflow-y-auto'}`}>
        <DialogBody className={`space-y-4 py-4 ${showCommonValues ? 'w-[650px] flex-shrink-0 overflow-y-auto' : ''}`}>
          {/* Show column name and description for DENORMALIZED columns */}
          {fieldFormData.type === 'DENORMALIZED' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="field-name">Column Name</Label>
                <IsolatedInput ref={nameRef} id="field-name" initialValue={initialFormData.name} placeholder="Enter column name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-description">Description</Label>
                <IsolatedTextarea ref={descriptionRef} initialValue={initialFormData.description} placeholder="Enter column description" rows={3} />
              </div>
            </>
          )}

          {/* Only show column name, description, and type for non-FOREIGN_KEY, non-DENORMALIZED columns */}
          {fieldFormData.type !== 'FOREIGN_KEY' && fieldFormData.type !== 'DENORMALIZED' && (
            <>
              <div className="space-y-2">
                <Label htmlFor="field-name">Column Name</Label>
                <IsolatedInput ref={nameRef} id="field-name" initialValue={initialFormData.name} placeholder="Enter column name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-description">Description</Label>
                <IsolatedTextarea ref={descriptionRef} initialValue={initialFormData.description} placeholder="Enter column description" rows={3} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="field-type">Column Type</Label>
                <Select
                  value={fieldFormData.type}
                  onValueChange={(value) => {
                    setFieldFormData({ ...fieldFormData, type: value });

                    // If changing to FOREIGN_KEY and we don't have relationship data, initialize it
                    if (value === 'FOREIGN_KEY' && !relationshipFormData) {
                      setRelationshipFormData({
                        child_node_id: currentNodeId || '',
                        parent_node_id: '',
                        name: '',
                        description: '',
                        child_foreign_key_name: nameRef.current?.getValue() || fieldFormData.name || '',
                        parent_lookup_logic_gate: 'AND',
                        parent_lookups: [],
                      });
                    } else if (value !== 'FOREIGN_KEY') {
                      // If changing away from FOREIGN_KEY, clear relationship data
                      setRelationshipFormData(null);
                    }
                  }}
                >
                  <SelectTrigger id="field-type">
                    <SelectValue placeholder="Select column type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="STRING">STRING</SelectItem>
                    <SelectItem value="INT">INT</SelectItem>
                    <SelectItem value="DECIMAL">DECIMAL</SelectItem>
                    <SelectItem value="DATE">DATE</SelectItem>
                    <SelectItem value="DATETIME">DATETIME</SelectItem>
                    <SelectItem value="DATETIME_TZ">DATETIME_TZ</SelectItem>
                    <SelectItem value="BOOLEAN">BOOLEAN</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Merge Behavior - Only for SUMMARIZE grouping behavior */}
              {groupingBehavior === 'SUMMARIZE' && (
                <div className="space-y-2">
                  <Label>Merge Behavior</Label>
                  {fieldFormData.part_of_composite_key ? (
                    <p className="text-sm text-muted-foreground">Data will be summarized using this column</p>
                  ) : (
                    <Select
                      value={fieldFormData.merge_behavior || 'PICK_ONE'}
                      onValueChange={(value) => setFieldFormData({ ...fieldFormData, merge_behavior: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select merge behavior" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PICK_ONE">Pick One</SelectItem>
                        <SelectItem value="SUM">Sum</SelectItem>
                        <SelectItem value="MAX">Max</SelectItem>
                        <SelectItem value="MIN">Min</SelectItem>
                        <SelectItem value="AVG">Average</SelectItem>
                        <SelectItem value="COUNT_DISTINCT">Count Distinct</SelectItem>
                        <SelectItem value="CONCAT">Concat</SelectItem>
                        <SelectItem value="COUNT_TRUE">Count True</SelectItem>
                        <SelectItem value="COUNT_FALSE">Count False</SelectItem>
                        <SelectItem value="BOOLOR_AGG">Boolean Or</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
              )}
            </>
          )}

          {/* Relationship Configuration Section - Only for FOREIGN_KEY */}
          {fieldFormData.type === 'FOREIGN_KEY' && relationshipFormData && (
            <div className="space-y-4 ">

              {/* Parent Node Selection */}
              <div className="space-y-2">
                <Label htmlFor="parent-node">Parent Node</Label>
                <Combobox
                  options={parentNodeOptions}
                  value={relationshipFormData.parent_node_id || ''}
                  onValueChange={(value) => {
                    setRelationshipFormData({
                      ...relationshipFormData,
                      parent_node_id: value,
                      // Clear parent_lookups when parent changes
                      parent_lookups: [],
                    });
                  }}
                  placeholder="Select parent node..."
                />
              </div>

              {/* Relationship Name */}
              <div className="space-y-2">
                <Label htmlFor="relationship-name">Relationship Name</Label>
                <Input
                  id="relationship-name"
                  value={relationshipFormData.name || ''}
                  onChange={(e) =>
                    setRelationshipFormData({
                      ...relationshipFormData,
                      name: e.target.value,
                    })
                  }
                  placeholder="Enter relationship name"
                />
              </div>

              {/* Relationship Description */}
              <div className="space-y-2">
                <Label htmlFor="relationship-description">Description</Label>
                <Textarea
                  id="relationship-description"
                  value={relationshipFormData.description || ''}
                  onChange={(e) =>
                    setRelationshipFormData({
                      ...relationshipFormData,
                      description: e.target.value,
                    })
                  }
                  placeholder="Enter relationship description"
                  rows={3}
                />
              </div>

              {/* Connection Parameters */}
              {relationshipFormData.parent_node_id && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <Label>Connection Parameters</Label>
                    {(!relationshipFormData.parent_lookups || relationshipFormData.parent_lookups.length === 0) && (
                      <span title="Relationship is missing connection parameters">
                        <AlertTriangle className="h-4 w-4 text-amber-500" />
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mb-2">
                    Define how columns from the parent and child nodes are matched
                  </p>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[45%]">Parent Column</TableHead>
                        <TableHead className="w-[45%]">Child Column</TableHead>
                        <TableHead className="w-[10%] text-right">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setRelationshipFormData({
                                ...relationshipFormData,
                                parent_lookups: [
                                  ...(relationshipFormData.parent_lookups || []),
                                  { parent_field_id: '', child_field_id: '' },
                                ],
                              });
                            }}
                            className="h-8 w-8 p-0"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {!relationshipFormData.parent_lookups || relationshipFormData.parent_lookups.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                            No connection parameters. Click the + button to add one.
                          </TableCell>
                        </TableRow>
                      ) : (
                        relationshipFormData.parent_lookups.map((lookup, idx) => {
                          const parentNode = allNodes.find(
                            (n) => n.id === relationshipFormData.parent_node_id
                          );
                          const parentFieldOptions: ComboboxOption[] = parentNode?.fields
                            ? parentNode.fields.map((f) => ({
                                value: f.id || f.name || '',
                                label: f.name || f.label || 'Unnamed Column',
                                description: f.type || '',
                              }))
                            : [];
                          const childFieldOptions: ComboboxOption[] = currentNodeFields
                            ? currentNodeFields
                                .filter((f) => {
                                  const isCalculated = (f as any).advanced_mode === true;
                                  return f.type !== 'FOREIGN_KEY' && !isCalculated && f.type !== 'DENORMALIZED';
                                })
                                .map((f) => ({
                                  value: f.id || f.name || '',
                                  label: f.name || f.label || 'Unnamed Column',
                                  description: f.type || '',
                                }))
                            : [];

                          return (
                            <TableRow key={idx}>
                              <TableCell>
                                <Combobox
                                  options={parentFieldOptions}
                                  value={lookup.parent_field_id || ''}
                                  onValueChange={(value) => {
                                    const newLookups = [...(relationshipFormData.parent_lookups || [])];
                                    newLookups[idx] = { ...newLookups[idx], parent_field_id: value };
                                    setRelationshipFormData({
                                      ...relationshipFormData,
                                      parent_lookups: newLookups,
                                    });
                                  }}
                                  placeholder="Select parent column..."
                                />
                              </TableCell>
                              <TableCell>
                                <Combobox
                                  options={childFieldOptions}
                                  value={lookup.child_field_id || ''}
                                  onValueChange={(value) => {
                                    const newLookups = [...(relationshipFormData.parent_lookups || [])];
                                    newLookups[idx] = { ...newLookups[idx], child_field_id: value };
                                    setRelationshipFormData({
                                      ...relationshipFormData,
                                      parent_lookups: newLookups,
                                    });
                                  }}
                                  placeholder="Select child column..."
                                />
                              </TableCell>
                              <TableCell className="text-right">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    const newLookups = [...(relationshipFormData.parent_lookups || [])];
                                    newLookups.splice(idx, 1);
                                    setRelationshipFormData({
                                      ...relationshipFormData,
                                      parent_lookups: newLookups,
                                    });
                                  }}
                                  className="h-8 w-8 p-0"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* Denormalized Configuration Section - Only for DENORMALIZED */}
          {fieldFormData.type === 'DENORMALIZED' && editingField && (
            <div className="space-y-4 border-t pt-4">
              <div>
                <Label className="text-base font-semibold">Denormalized Column Configuration</Label>
                <p className="text-xs text-muted-foreground mt-1">
                  Select a foreign key relationship and choose columns from the parent node to bring in
                </p>
              </div>

              {/* Foreign Key (Relationship) Selection */}
              <div className="space-y-2">
                <Label htmlFor="denormalized-relationship">Foreign Key</Label>
                <Combobox
                  options={foreignKeyRelationshipOptions}
                  value={(editingField as PipelineNodeField).relationship_id || ''}
                  onValueChange={(value) => {
                    onDenormalizedRelationshipChange({
                      oldRelationshipId: (editingField as PipelineNodeField).relationship_id,
                      newRelationshipId: value,
                      fieldName: nameRef.current?.getValue() || fieldFormData.name,
                      editingField,
                    });
                  }}
                  placeholder="Select foreign key relationship..."
                />
              </div>

              {/* Parent Node Columns Selection */}
              {(editingField as PipelineNodeField).relationship_id && (
                (() => {
                  const relationshipId = (editingField as PipelineNodeField).relationship_id!;
                  const isPending = isPendingRelationshipId(relationshipId);
                  const pendingFieldKey = getPendingFieldKey(relationshipId);

                  // Get relationship data from the appropriate source
                  let relationship: Partial<PipelineNodeRelationship> | undefined;
                  if (isPending && pendingFieldKey) {
                    relationship = pendingRelationships.get(pendingFieldKey);
                  } else if (relationshipsToSave.has(relationshipId)) {
                    relationship = relationshipsToSave.get(relationshipId);
                  } else if (getRelationship) {
                    relationship = getRelationship(relationshipId);
                  }

                  const parentNode = relationship
                    ? allNodes.find((n) => n.id === relationship.parent_node_id)
                    : null;
                  const currentDenormalizedField = relationship?.denormalized_fields?.find(
                    (df) => df.output_field_name === originalEditingFieldName
                  );
                  const selectedParentFieldId = currentDenormalizedField?.parent_field_id || '';

                  return parentNode ? (
                    <div className="space-y-2">
                      <Label>Parent Node Column</Label>
                      <p className="text-xs text-muted-foreground mb-2">
                        Select one column from {parentNode.label || parentNode.name} to include in this denormalized field
                      </p>
                      <Combobox
                        options={parentNode.fields?.map((f) => ({
                          value: f.id || f.name || '',
                          label: f.name || f.label || 'Unnamed Column',
                          description: f.type || '',
                        })) || []}
                        value={selectedParentFieldId}
                        onValueChange={(value) => {
                          const currentName = nameRef.current?.getValue() || fieldFormData.name;
                          onDenormalizedParentColumnChange({
                            relationshipId,
                            parentFieldId: value,
                            fieldName: currentName,
                            originalFieldName: originalEditingFieldName || '',
                          });

                          // Update local originalEditingFieldName to the new name so subsequent
                          // lookups find the entry we just added/updated
                          if (currentName !== originalEditingFieldName) {
                            setOriginalEditingFieldName(currentName);
                          }
                        }}
                        placeholder="Select a column from parent node..."
                      />
                    </div>
                  ) : null;
                })()
              )}
            </div>
          )}

          {/* Calculated Field Formula Section */}
          {editingField && (editingField as any).advanced_mode === true && (
            <div className="space-y-2 border-t pt-4">
              <Label htmlFor="calculated-field-formula">Formula</Label>
              <p className="text-xs text-muted-foreground mb-2">
                Edit the SQL formula for this calculated column
              </p>
              <IsolatedTextarea
                ref={customSqlRef}
                id="calculated-field-formula"
                initialValue={initialFormData.custom_sql || ''}
                placeholder="Enter SQL formula..."
                rows={8}
                className="font-mono text-sm"
              />
            </div>
          )}

          {/* Mapped Columns Section */}
          {fieldFormData.type !== 'FOREIGN_KEY' &&
           fieldFormData.type !== 'DENORMALIZED' &&
           editingField &&
           (editingField as any).advanced_mode !== true && (
          <div className="space-y-2 border-t pt-4">
            <Label>Mapped Columns</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Columns from upstream nodes that map to this column
            </p>

            {/* Add Column Combobox */}
            {upstreamNodes.length > 0 && (
              <Combobox
                options={mappedFieldComboboxOptions}
                value=""
                onValueChange={(value) => {
                  if (!value) return;
                  const [nodeId, fieldId] = value.split(':');
                  const upstreamNode = upstreamNodes.find((n) => n.id === nodeId);
                  const field = upstreamNode?.fields?.find((f) => (f.id || f.name) === fieldId);

                  if (upstreamNode && field) {
                    const newMapOption: PipelineNodeMapOption = {
                      id: `${nodeId}:${field.id || field.name || Date.now()}`,
                      source_node_id: nodeId,
                      attribute_key: field.name || field.label,
                      attribute_id: field.id || field.name,
                    };
                    setFieldFormData({
                      ...fieldFormData,
                      map_options: [...(fieldFormData.map_options || []), newMapOption],
                    });
                  }
                }}
                placeholder="Add a mapped column from upstream nodes..."
              />
            )}

            {/* Mapped Columns List with Drag and Drop */}
            {fieldFormData.map_options && fieldFormData.map_options.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={(event: DragEndEvent) => {
                  const { active, over } = event;
                  if (!over || active.id === over.id) return;

                  const oldIndex = fieldFormData.map_options!.findIndex(
                    (option: PipelineNodeMapOption) => getMapOptionKey(option) === active.id
                  );
                  const newIndex = fieldFormData.map_options!.findIndex(
                    (option: PipelineNodeMapOption) => getMapOptionKey(option) === over.id
                  );

                  if (oldIndex !== -1 && newIndex !== -1) {
                    const newMapOptions = arrayMove(fieldFormData.map_options!, oldIndex, newIndex);
                    setFieldFormData({ ...fieldFormData, map_options: newMapOptions });
                  }
                }}
              >
                <SortableContext
                  items={fieldFormData.map_options.map((option: PipelineNodeMapOption) => getMapOptionKey(option))}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2 mt-2">
                    {fieldFormData.map_options.map((mapOption: PipelineNodeMapOption) => {
                      const mapOptionKey = getMapOptionKey(mapOption);
                      const upstreamNode = upstreamNodes.find(
                        (n) => n.id === mapOption.source_node_id
                      );
                      const mappedField = upstreamNode?.fields?.find(
                        (f) => (f.id || f.name) === mapOption.attribute_id || (f.name || f.label) === mapOption.attribute_key
                      );

                      return (
                        <SortableMappedFieldItem
                          key={mapOptionKey}
                          id={mapOptionKey}
                          nodeName={upstreamNode?.label || upstreamNode?.name || 'Unknown Node'}
                          fieldName={mappedField?.name || mappedField?.label || mapOption.attribute_key || 'Unknown Column'}
                          fieldType={mappedField?.type || 'Unknown'}
                          onRemove={() => {
                            const newMapOptions = fieldFormData.map_options!.filter(
                              (option: PipelineNodeMapOption) => getMapOptionKey(option) !== mapOptionKey
                            );
                            setFieldFormData({ ...fieldFormData, map_options: newMapOptions });
                          }}
                        />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <p className="text-sm text-muted-foreground py-2">No mapped columns</p>
            )}
          </div>
          )}

          {/* Transformer Section */}
          {fieldFormData.type !== 'FOREIGN_KEY' &&
           fieldFormData.type !== 'DENORMALIZED' &&
           editingField &&
           (editingField as any).advanced_mode !== true && (
          <div className="space-y-2 border-t pt-4">
            <Label>Transformer</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Apply a transformation function to clean or modify the column values
            </p>

            <Select
              value={fieldFormData.transformer || '__none__'}
              onValueChange={(value) => {
                const newTransformer = value === '__none__' ? undefined : value;
                setFieldFormData({
                  ...fieldFormData,
                  transformer: newTransformer,
                  custom_transform_sql: newTransformer === 'CUSTOM' ? (fieldFormData.custom_transform_sql || '') : undefined,
                });
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select a transformer (optional)..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__" className="text-muted-foreground">None</SelectItem>
                {TransformerOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value} title={option.description}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Custom Transform SQL Textarea */}
            {fieldFormData.transformer === 'CUSTOM' && (
              <div className="space-y-2">
                <Label htmlFor="custom-transform-sql">Custom Transform SQL</Label>
                <p className="text-xs text-muted-foreground mb-2">
                  Write custom Snowflake SQL to transform the column values
                </p>
                <IsolatedTextarea
                  ref={customTransformSqlRef}
                  id="custom-transform-sql"
                  initialValue={initialFormData.custom_transform_sql || ''}
                  placeholder="e.g., UPPER([VALUE]) or TRIM([VALUE]). [VALUE] will be replaced by the column name at run time."
                  rows={4}
                  className="font-mono text-sm"
                />
              </div>
            )}

            {/* Translate Table */}
            {fieldFormData.transformer === 'TRANSLATE' && (
              <Card className="mt-4">
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">Translation Mappings</CardTitle>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleAutoNormalize}
                      disabled={isAutoNormalizing || !fieldFormData.map_options || fieldFormData.map_options.length === 0}
                    >
                      {isAutoNormalizing ? (
                        <><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Normalizing...</>
                      ) : (
                        <><Sparkles className="h-3 w-3 mr-1" /> Auto-Normalize</>
                      )}
                    </Button>
                  </div>
                  <CardDescription>
                    Define how source values should be translated to target values
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <IsolatedTranslationsTable ref={translationsRef} initialValue={initialFormData.translations || []} />
                </CardContent>
              </Card>
            )}
          </div>
          )}
        </DialogBody>
        {showCommonValues && (
          <CommonValuesPanel
            mapOptions={fieldFormData.map_options || []}
            isTranslate={fieldFormData.transformer === 'TRANSLATE'}
            onAddTranslation={handleAddTranslationFromPanel}
          />
        )}
        </div>
        <DialogFooter className="sm:justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            {fieldFormData.map_options && fieldFormData.map_options.length > 0 &&
              fieldFormData.type !== 'FOREIGN_KEY' &&
              fieldFormData.type !== 'DENORMALIZED' &&
              editingField && (editingField as any).advanced_mode !== true && (
              <>
                <Switch
                  checked={showCommonValues}
                  onCheckedChange={setShowCommonValues}
                  id="show-values-toggle"
                />
                <Label htmlFor="show-values-toggle" className="text-sm cursor-pointer">Show Values</Label>
              </>
            )}
          </div>
          <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
          >
            Save Changes
          </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
